// InsightsList.js — the view's headline facts (latest scorigami, most
// common result, biggest margin, ...) as static cards in the side panel.
// Each card with a scoreline opens that cell's details.

import React, { useMemo } from "react";
import { Avatar, Box, Typography } from "@mui/material";
import BoltIcon from "@mui/icons-material/Bolt";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { BRAND } from "../../theme";
import { EYEBROW_SX } from "./HeatmapLegend";
import { displayName, formatDate, occurrenceLine, orientOccurrence, ALL_LABEL, HOME_AWAY } from "./utils";

// Build the fact list (stable across renders unless data/season changes).
function buildFacts({ scoreSummaryMap, scoreMap, isAllSeasons, season, axes }) {
  const facts = [];
  const summary = Object.values(scoreSummaryMap || {});

  // 1. Latest scorigami — all-time or seasonal (count==1)
  if (isAllSeasons) {
    let best = null;
    for (const s of summary) {
      const d = s?.firstOccurrence?.utcDate;
      if (!d) continue;
      if (!best || new Date(d) > new Date(best.firstOccurrence.utcDate)) best = s;
    }
    if (best) {
      const f = best.firstOccurrence;
      const o = orientOccurrence(f, axes);
      facts.push({
        key: `latest-${best.score}`,
        icon: BoltIcon,
        eyebrow: "Latest Scorigami",
        text: occurrenceLine(f, axes),
        caption: formatDate(f.utcDate),
        high: o.leftTeam,
        low: o.rightTeam,
        score: best.score,
      });
    }
  } else {
    // Single-season: scorelines that happened exactly once this season
    let bestDate = null;
    let bestMatch = null;
    let bestKey = null;
    for (const [key, list] of Object.entries(scoreMap || {})) {
      if (!list || list.length !== 1) continue;
      const m = list[0];
      const dt = new Date(m.utcDate);
      if (!bestDate || dt > bestDate) {
        bestDate = dt;
        bestMatch = m;
        bestKey = key;
      }
    }
    if (bestMatch) {
      const o = orientOccurrence(bestMatch, axes);
      facts.push({
        key: `season-latest-${bestKey}`,
        icon: BoltIcon,
        eyebrow: `Latest in ${season}`,
        text: occurrenceLine(bestMatch, axes),
        caption: formatDate(bestMatch.utcDate),
        high: o.leftTeam,
        low: o.rightTeam,
        score: bestKey,
      });
    }
  }

  // 2. Most common scoreline (uses summary for all-time; scoreMap for season)
  if (isAllSeasons) {
    let top = null;
    for (const s of summary) {
      if (!top || (s?.count || 0) > (top.count || 0)) top = s;
    }
    if (top) {
      facts.push({
        key: `common-${top.score}`,
        icon: TrendingUpIcon,
        eyebrow: "Most Common",
        text: `${top.score.replace("-", "–")} — ${top.count.toLocaleString()} matches`,
        caption: axes === HOME_AWAY ? "home–away, all-time" : "Premier League all-time",
        score: top.score,
      });
    }
  } else {
    let topKey = null;
    let topLen = 0;
    for (const [k, list] of Object.entries(scoreMap || {})) {
      if (!list) continue;
      if (list.length > topLen) {
        topLen = list.length;
        topKey = k;
      }
    }
    if (topKey) {
      facts.push({
        key: `common-${topKey}`,
        icon: TrendingUpIcon,
        eyebrow: `Most Common in ${season}`,
        text: `${topKey.replace("-", "–")} — ${topLen} match${topLen === 1 ? "" : "es"}`,
        caption: "this season",
        score: topKey,
      });
    }
  }

  // 3. Biggest winning margin on record (from summary — uses firstOccurrence)
  if (isAllSeasons && summary.length) {
    let best = null;
    let bestMargin = -1;
    for (const s of summary) {
      const margin = (s.firstOccurrence?.highScore ?? 0) - (s.firstOccurrence?.lowScore ?? 0);
      if (margin > bestMargin) {
        bestMargin = margin;
        best = s;
      }
    }
    if (best && bestMargin > 0) {
      const f = best.firstOccurrence;
      facts.push({
        key: `margin-${best.score}`,
        icon: WhatshotIcon,
        eyebrow: "Biggest Margin",
        text: `${displayName(f.highTeam?.name)} ${f.highScore}–${f.lowScore} ${displayName(f.lowTeam?.name)}`,
        caption: `${bestMargin}-goal win · ${formatDate(f.utcDate)}`,
        high: f.highTeam,
        low: f.lowTeam,
        score: best.score,
      });
    }
  }

  return facts;
}

function FactCard({ fact, onClick }) {
  const Icon = fact.icon;
  const clickable = Boolean(fact.score && onClick);
  return (
    <Box
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? () => onClick(fact.score) : undefined}
      onKeyDown={(e) => {
        if (clickable && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick(fact.score);
        }
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.5,
        borderRadius: 2,
        background: "rgba(255,255,255,0.035)",
        border: "1px solid rgba(255,255,255,0.07)",
        cursor: clickable ? "pointer" : "default",
        transition: "border-color 160ms ease, transform 160ms ease",
        outline: "none",
        "&:hover, &:focus-visible": clickable ? { borderColor: BRAND.cyan, transform: "translateY(-1px)" } : {},
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          flexShrink: 0,
          borderRadius: "10px",
          display: "grid",
          placeItems: "center",
          background: BRAND.gradient,
          color: "#fff",
          boxShadow: "0 4px 14px rgba(111,93,245,0.35)",
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ ...EYEBROW_SX, color: BRAND.cyan, fontSize: 9.5, mb: 0.5 }}>{fact.eyebrow}</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          {fact.high && <Avatar src={fact.high.image} alt="" sx={{ width: 18, height: 18 }} />}
          <Typography
            sx={{
              color: "#fff",
              fontSize: 13.5,
              fontWeight: 700,
              lineHeight: 1.3,
              minWidth: 0,
              // Up to two lines, then ellipsis.
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {/* Word joiners keep a score like "0–8" on one line when the text wraps. */}
            {fact.text.replace(/(\d)–(\d)/g, "$1\u2060–\u2060$2")}
          </Typography>
          {fact.low && <Avatar src={fact.low.image} alt="" sx={{ width: 18, height: 18 }} />}
        </Box>
        {fact.caption && (
          <Typography sx={{ color: BRAND.textSecondary, fontSize: 12, mt: 0.25 }}>{fact.caption}</Typography>
        )}
      </Box>
      {clickable && <ChevronRightIcon sx={{ color: BRAND.textSecondary, fontSize: 20 }} />}
    </Box>
  );
}

export default function InsightsList({ scoreSummaryMap, scoreMap, season, axes, onClick }) {
  const isAllSeasons = !season || season === ALL_LABEL;
  const facts = useMemo(
    () => buildFacts({ scoreSummaryMap, scoreMap, isAllSeasons, season, axes }),
    [scoreSummaryMap, scoreMap, isAllSeasons, season, axes]
  );
  if (!facts.length) return null;
  return (
    <Box>
      <Typography sx={{ ...EYEBROW_SX, mb: 1.25 }}>Highlights</Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {facts.map((f) => (
          <FactCard key={f.key} fact={f} onClick={onClick} />
        ))}
      </Box>
    </Box>
  );
}
